import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import AsyncSelect from 'react-select/async';
import { ThemeContext } from 'styled-components';
import FieldWrapper from '../../FieldWrapper';
import { useCustomStyles } from './hooks';
import IndicatorsContainer from './IndicatorsContainer';
import Option from './Option';
import Placeholder from './Placeholder';
import { BaseSelectItem, AsyncSelectProps } from './types';
import ValueContainer from './ValueContainer';

function AsyncSelectBase<
  T = string,
  P extends BaseSelectItem<T> = BaseSelectItem<T>
>({
  renderOptionContent,
  renderPlaceholderContent,
  renderControlContent,
  icon,
  withoutIndicatorRotation,
  notSeparated = false,
  isError = false,
  errorMessage,
  label,
  disabled,
  options,
  value,
  onChange,
  hideErrorMessageWrapper = false,
  loadOptions,
  placeholder,
  ...props
}: AsyncSelectProps<T, P>): ReactComponentReturnType {
  const intl = useIntl();
  const theme = useContext(ThemeContext);
  const resolvedPlaceholder =
    placeholder ??
    intl.formatMessage({ id: 'forms.fields.select', defaultMessage: 'Select...' });
  const customStyles = useCustomStyles<P>(notSeparated, isError, true);
  const selectedItem = options?.find((option) => option.value === value);

  return (
    <FieldWrapper
      error={isError ? errorMessage : ''}
      hideErrorMessageWrapper={hideErrorMessageWrapper}
      control={
        <AsyncSelect<P>
          placeholder={resolvedPlaceholder}
          onChange={
            onChange
              ? (option) => {
                  if (option && option.value !== undefined) {
                    onChange(option.value);
                  }
                }
              : undefined
          }
          value={selectedItem || null}
          components={{
            ValueContainer: (valueContainerProps) => (
              <ValueContainer
                {...{ renderControlContent, ...valueContainerProps }}
              />
            ),
            IndicatorsContainer: (indicatorsContainerProps) => (
              <IndicatorsContainer
                {...{
                  icon,
                  withoutIndicatorRotation,
                  ...indicatorsContainerProps,
                }}
              />
            ),
            Option: (optionProps) => (
              <Option {...{ renderOptionContent, ...optionProps }} />
            ),
            Placeholder: (placeholderProps) => (
              <Placeholder
                {...{ renderPlaceholderContent, ...placeholderProps }}
              />
            ),
          }}
          menuPlacement='auto'
          isRtl={theme.direction === 'rtl'}
          styles={customStyles}
          isDisabled={disabled}
          loadOptions={loadOptions}
          {...{ ...props, defaultOptions: options }}
        />
      }
      {...{ label }}
    />
  );
}

export default AsyncSelectBase;
